import React, { useState, useRef, useImperativeHandle, useCallback } from 'react';
import { Autocomplete, TextField } from '@mui/material';

const ITEM_HEIGHT = 40;
const LISTBOX_PADDING = 8;
const DEFAULT_LISTBOX_HEIGHT = 320;

/**
 * MUI-only virtualized listbox for Autocomplete.
 * Renders only visible items based on scroll position (no react-window or other libs).
 */
const VirtualListbox = React.forwardRef(function VirtualListbox(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const totalHeight = itemCount * ITEM_HEIGHT + 2 * LISTBOX_PADDING;
  const listboxHeight = Math.min(DEFAULT_LISTBOX_HEIGHT, totalHeight);

  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef(null);

  useImperativeHandle(ref, () => scrollRef.current);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const startIndex = Math.max(0, Math.floor((scrollTop - LISTBOX_PADDING) / ITEM_HEIGHT));
  const visibleCount = Math.ceil(listboxHeight / ITEM_HEIGHT) + 2;
  const endIndex = Math.min(itemCount - 1, startIndex + visibleCount);
  const visibleItems = itemData.slice(startIndex, endIndex + 1);
  const offsetY = startIndex * ITEM_HEIGHT + LISTBOX_PADDING;

  return (
    <ul
      ref={(node) => {
        scrollRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      role="listbox"
      onScroll={handleScroll}
      style={{
        height: listboxHeight,
        overflow: 'auto',
        padding: 0,
        margin: 0,
        listStyle: 'none',
        position: 'relative',
      }}
      {...other}
    >
      <li
        style={{
          height: totalHeight,
          position: 'relative',
          listStyle: 'none',
          margin: 0,
          padding: 0,
        }}
        aria-hidden="true"
      >
        <ul
          style={{
            position: 'absolute',
            top: offsetY,
            left: 0,
            right: 0,
            margin: 0,
            padding: 0,
            listStyle: 'none',
          }}
          role="presentation"
        >
          {visibleItems.map((child, i) =>
            React.isValidElement(child)
              ? React.cloneElement(child, {
                  key: startIndex + i,
                  style: {
                    ...(child.props.style || {}),
                    height: ITEM_HEIGHT,
                    maxHeight: ITEM_HEIGHT,
                  },
                })
              : (
              <li key={startIndex + i} style={{ height: ITEM_HEIGHT }}>
                {child}
              </li>
                )
          )}
        </ul>
      </li>
    </ul>
  );
});

/**
 * Searchable, keyboard-friendly Autocomplete with MUI-only virtualized list.
 * For large option lists (Country, State, City). No third-party virtualization.
 * When options are empty, freeSolo allows free text (e.g. non-India location).
 */
const VirtualizedAutocomplete = ({
  options = [],
  value = '',
  onChange,
  label,
  disabled = false,
  sx = {},
  size = 'small',
  placeholder = 'Select...',
}) => {
  const hasOptions = options.length > 0;
  const getOptionLabel = useCallback((option) => (typeof option === 'string' ? option : option?.name ?? ''), []);
  const valueOption =
    value != null && value !== ''
      ? (hasOptions ? options.find((o) => getOptionLabel(o) === value) : value)
      : null;
  const displayValue = hasOptions ? valueOption : value;

  const isOptionEqualToValue = useCallback(
    (option, v) => getOptionLabel(option) === (typeof v === 'string' ? v : v ? getOptionLabel(v) : ''),
    [getOptionLabel]
  );

  return (
    <Autocomplete
      freeSolo={!hasOptions}
      options={options}
      value={displayValue}
      onInputChange={!hasOptions ? (_, v) => onChange(v ?? '') : undefined}
      onChange={(_, newValue) => {
        const next = typeof newValue === 'string' ? newValue : (newValue && (typeof newValue === 'object' && 'name' in newValue) ? newValue.name : '');
        onChange(next ?? '');
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      disabled={disabled}
      size={size}
      ListboxComponent={hasOptions ? VirtualListbox : undefined}
      ListboxProps={hasOptions ? { style: { overflowX: 'hidden' } } : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          sx={{ ...sx }}
        />
      )}
      sx={{ width: '100%', ...sx }}
    />
  );
};

export default VirtualizedAutocomplete;
